import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { classNames } from "primereact/utils";
import { useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";
import NoInfo from "../../components/NoInfo";
import PrimeTableFooter from "../../components/PrimeTableFooter";
import { useEditableFlag } from "../../hooks/useEditableFlag";
import { SystemLoading, checkLoading } from "../../store/loadingStore";
import { getSubItemsByItemNo } from "../../utility/service/api-service";
import { getBoudaryDeviceList } from "../../utility/service/boundaryDevice-service";
import { getCompanyPlaceList, queryClassNoTypeList, queryClassProcessList } from "../../utility/service/carbon-service";
import { checkDpSuccess, getCompanyType, getFormErrorMessage } from "../../utility/service/tool-service";

const BoundaryDevice = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    setItemObj,
    setCompanyName,
    setTabAction,
    currentCompanySeq,
    setCurrentCompanySeq,
    placeList,
    setPlaceList,
    queryData,
    setQueryData,
  } = useOutletContext();
  const isEditable = useEditableFlag("F10405"); // control edit by F10103
  const [, loading] = useContext(SystemLoading);
  const [companyPlaceList, setCompanyPlaceList] = useState([]);
  const [typeList, setTypeList] = useState([]);
  const [materialTypeList, setMaterialTypeList] = useState([]);
  const [rows, setRows] = useState([]);
  const [lastRowSeq, setLastRowSeq] = useState("");

  const {
    register,
    handleSubmit,
    watch,
    clearErrors,
    getValues,
    setValue,
    resetField,
    reset: resetForm,
    control,
    trigger,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {},
    //錯誤驗證時機
    mode: "all",
  });

  useEffect(() => {
    const isFromChildPage = sessionStorage.getItem("fromBoundaryDeviceChildPage");
    if (currentCompanySeq !== "" && isFromChildPage) {
      axios_getBoudaryDeviceList({
        companySeq: currentCompanySeq,
        queryPlaceSeq: queryData?.queryPlaceSeq,
        queryTypeSeq: queryData?.queryTypeSeq,
        queryClassNo: queryData?.queryClassNo,
      });
      setValue("companySeq", currentCompanySeq);
      Object.entries(queryData).forEach(([key, value]) => {
        setValue(key, value);
      });
      sessionStorage.removeItem("fromBoundaryDeviceChildPage");
    }
  }, [currentCompanySeq]);

  useEffect(() => {
    axios_getCompanyPlaceList();
    axios_queryClassProcessList({
      class: "C",
    });
    axios_getSubItemsByItemNo("CFV_PLAN_MATERIAL", 13).then(setMaterialTypeList);
  }, []);

  const axios_getSubItemsByItemNo = (itemNo, orderNo) => {
    return getSubItemsByItemNo(itemNo, orderNo).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.subItems;
        return data.map((item, index) => ({
          ...item,
          index,
        }));
      }
      return [];
    });
  };

  //組織邊界廠商清單
  const axios_getCompanyPlaceList = () => {
    checkLoading(loading);
    getCompanyPlaceList()
      .then((res) => {
        if (checkDpSuccess(res)) {
          let data = res.data.RespBody.companyPlaceList;
          setCompanyPlaceList(data);

          if (getCompanyType() === "A") {
            const firstCompany = data[0];
            setValue("companySeq", firstCompany.companySeq);
            setCurrentCompanySeq(firstCompany.companySeq);
            setCompanyName(firstCompany?.companyName);
            setPlaceList(firstCompany?.placeList);
            axios_getBoudaryDeviceList({
              companySeq: firstCompany.companySeq,
            });
          }
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  // 邊界設備維護查詢
  const axios_getBoudaryDeviceList = (data) => {
    checkLoading(loading);
    getBoudaryDeviceList(data)
      .then((res) => {
        if (checkDpSuccess(res)) {
          let data = res.data.RespBody.placeDeviceList;
          setRows(data ?? []);
          if (data?.length > 0) {
            setLastRowSeq(data[data?.length - 1].placeDeviceSeq);
          }
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  // 範疇/類別 criterion 0-> S ; criterion 1 -> C
  const axios_queryClassProcessList = (reqBody) => {
    queryClassProcessList(reqBody).then((res) => {
      if (checkDpSuccess(res)) {
        let result = res.data.RespBody;
        (result?.classList ?? []).forEach((e) => {
          axios_queryClassNoTypeList(e.classNo).then((res) => {
            const newItems = res || [];
            setTypeList((prev) => [...prev, ...newItems]); // 疊加
          });
        });
      }
    });
  };

  // 排放源型式
  const axios_queryClassNoTypeList = (classNo, typeSeq) => {
    return queryClassNoTypeList({ classNo }).then((res) => {
      if (checkDpSuccess(res)) {
        return res.data.RespBody?.typeList ?? [];
      }
      return [];
    });
  };

  const checkKeyDown = (e) => {
    if (e.key === "Enter") e.preventDefault();
  };

  const btnReadMore = () => {
    axios_getBoudaryDeviceList(
      {
        companySeq: getValues("companySeq"),
        classNo: getValues("classNo"),
        typeSeq: getValues("typeSeq"),
        placeDeviceSeq: lastRowSeq,
      },
      true,
    );
  };

  const onSubmit = (data) => {
    const reqBody = {
      companySeq: data.companySeq,
      classNo: data.queryClassNo,
      typeSeq: data.queryTypeSeq,
      placeSeq: data.queryPlaceSeq,
    };
    axios_getBoudaryDeviceList(reqBody);
    setQueryData({
      queryClassNo: data.queryClassNo,
      queryTypeSeq: data.queryTypeSeq,
      queryPlaceSeq: data.queryPlaceSeq,
    });
  };

  const enableFlagBodyTemplate = (rowData) => {
    if (rowData) {
      return <Tag severity={rowData.enableFlag === "Y" ? "success" : "danger"} value={rowData?.enableFlagName}></Tag>;
    }
  };

  const actionBodyTemplate = (rowData, props) => {
    if (rowData) {
      return (
        <Button
          tooltip={t("button.detail")}
          tooltipOptions={{ position: "top" }}
          rounded
          text
          severity="secondary"
          className="p-button-icon-only"
          onClick={() => {
            setItemObj(rowData);
            setTabAction("detail");
            navigate("detail");
          }}
        >
          <FontAwesomeIcon icon="fa-solid fa-eye" />
        </Button>
      );
    }
  };

  return (
    <>
      <div className="p-card bg-gray">
        <div className="p-card-body">
          <form onSubmit={handleSubmit(onSubmit)} onKeyDown={checkKeyDown} className="grid">
            {/* 組織名稱 */}
            <div className="col-12 lg:col-6">
              <label htmlFor="placeName" className="form-label required">
                {t("organName")}
              </label>
              <Controller
                name="companySeq"
                control={control}
                rules={{ required: { value: true, message: t("validation.required") } }}
                render={({ field, fieldState }) => (
                  <Dropdown
                    filter
                    disabled={getCompanyType() === "A"}
                    appendTo={"self"}
                    optionLabel="companyName"
                    optionValue="companySeq"
                    value={field.value}
                    virtualScrollerOptions={{ itemSize: 40 }}
                    defaultValue={""}
                    placeholder={t("plz_chs")}
                    name="companySeq"
                    control={control}
                    options={companyPlaceList}
                    className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                    onChange={(event) => {
                      field.onChange(event.value);
                      setValue("companySeq", event.value);
                      const filter = companyPlaceList.filter((item) => item.companySeq === watch("companySeq"))[0];
                      setValue("companyName", filter?.companyName);
                      setPlaceList(filter?.placeList);
                      setCurrentCompanySeq(event.value);
                      setQueryData({ ...queryData, queryPlaceSeq: "" });
                    }}
                  />
                )}
              />
              {getFormErrorMessage(errors, "companySeq")}
            </div>
            {/* 場所 */}
            <div className="col-12 lg:col-6">
              <label htmlFor="queryPlaceSeq" className="form-label">
                {t("place")}
              </label>
              <Controller
                name="queryPlaceSeq"
                control={control}
                render={({ field, fieldState }) => (
                  <Dropdown
                    filter
                    showClear
                    appendTo={"self"}
                    optionLabel="placeName"
                    optionValue="placeSeq"
                    value={field.value}
                    virtualScrollerOptions={{ itemSize: 40 }}
                    defaultValue={""}
                    placeholder={t("plz_chs")}
                    name="queryPlaceSeq"
                    control={control}
                    options={placeList}
                    className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                    onChange={(event) => {
                      field.onChange(event.value);
                    }}
                  />
                )}
              />
              {getFormErrorMessage(errors, "companySeq")}
            </div>
            {/* 型式序號  */}
            <div className="col-12 lg:col-6">
              <label htmlFor="queryClassNo" className="form-label">
                {t("emission_source_type")}
              </label>
              <Controller
                name="queryClassNo"
                control={control}
                render={({ field, fieldState }) => (
                  <Dropdown
                    filter
                    showClear
                    appendTo={"self"}
                    optionLabel="typeName"
                    optionValue="typeSeq"
                    value={field.value}
                    virtualScrollerOptions={{ itemSize: 40 }}
                    defaultValue={""}
                    placeholder={t("plz_chs_all")}
                    name="queryClassNo"
                    control={control}
                    options={typeList}
                    className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                    onChange={(event) => {
                      field.onChange(event.value);
                    }}
                  />
                )}
              />
              {getFormErrorMessage(errors, "classNo")}
            </div>
            {/* 原燃物料類別 */}
            <div className="col-12 lg:col-6">
              <label htmlFor="queryTypeSeq" className="form-label">
                {t("rawMaterial")}
              </label>
              <Controller
                name="queryTypeSeq"
                control={control}
                render={({ field, fieldState }) => (
                  <Dropdown
                    filter
                    showClear
                    appendTo={"self"}
                    optionLabel="subitemName"
                    optionValue="subitemNo"
                    value={field.value}
                    virtualScrollerOptions={{ itemSize: 40 }}
                    defaultValue={""}
                    placeholder={t("plz_chs_all")}
                    name="queryTypeSeq"
                    control={control}
                    options={materialTypeList}
                    className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                    onChange={(event) => {
                      field.onChange(event.value);
                    }}
                  />
                )}
              />
              {getFormErrorMessage(errors, "typeSeq")}
            </div>

            <div className="col-12 gap-2 flex-all-center">
              <Button type="submit" label={t("button.query")} />
              <Button
                disabled={!isEditable || !getValues("companySeq")}
                outlined
                type="button"
                onClick={() => {
                  clearErrors();
                  setTabAction("create");
                  setItemObj({});
                  navigate("detail");
                }}
                label={t("button.create")}
              />
            </div>
          </form>
        </div>
      </div>
      <div className="p-card">
        <div className="p-card-body">
          <DataTable
            stripedRows
            className="white-data-table hover-highlight no-shadow"
            value={rows}
            tableStyle={{ minWidth: "40rem" }}
            footer={
              rows.length > 0 && <PrimeTableFooter rowDataList={rows} isShowMore={true} btnReadMore={btnReadMore} />
            }
            emptyMessage={<NoInfo panel="table" />}
          >
            <Column style={{ minWidth: "150px" }} field="placeName" header={t("place")} />
            <Column style={{ minWidth: "150px" }} field="placeDeviceNo" header={t("placeDeviceNo")} />
            <Column
              style={{ minWidth: "150px" }}
              field="enableFlag"
              header={t("status")}
              body={enableFlagBodyTemplate}
            />
            <Column body={actionBodyTemplate} header={t("action")}></Column>
          </DataTable>
        </div>
      </div>
    </>
  );
};

export default BoundaryDevice;
